import { useEffect, useState } from 'react';
import { ChatProvider, useChatContext } from './hooks/useChat';
import Header from './components/Header';
import HomeView from './views/Home';
import ChatView from './views/Chat';
import HistoryView from './views/History';

type View = 'home' | 'chat' | 'history';

function AppContent() {
  const [view, setView] = useState<View>('home');
  const { activeConversation, startNewConversation } = useChatContext();

  // Switch to chat view when a conversation is active
  useEffect(() => {
    if (activeConversation) {
      setView('chat');
    }
  }, [activeConversation]);

  const handleNewChat = () => {
    startNewConversation();
    setView('chat');
  };

  const handleViewHistory = () => {
    setView('history');
  };

  const handleBack = () => {
    setView('home');
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <Header
        showBack={view !== 'home'}
        onBack={handleBack}
        onNewChat={handleNewChat}
        onHistory={handleViewHistory}
      />

      <main className="flex-1 overflow-hidden">
        {view === 'home' && (
          <HomeView onNewChat={handleNewChat} onViewHistory={handleViewHistory} />
        )}
        {view === 'chat' && <ChatView />}
        {view === 'history' && (
          <HistoryView
            onSelectConversation={() => setView('chat')}
            onNewChat={handleNewChat}
          />
        )}
      </main>
    </div>
  );
}

export default function App() {
  // Parse URL params for configuration
  const params = new URLSearchParams(window.location.search);
  const stackId = params.get('stackId') || '';
  const customerId = params.get('customerId') || '';
  const apiKey = params.get('apiKey') || undefined;

  // Parse theme if provided
  let theme = undefined;
  try {
    const themeParam = params.get('theme');
    if (themeParam) {
      theme = JSON.parse(themeParam);
    }
  } catch {
    // Ignore parsing errors
  }

  // Apply theme CSS variables
  useEffect(() => {
    if (theme) {
      const root = document.documentElement;
      if (theme.primaryColor) {
        root.style.setProperty('--fanks-primary', theme.primaryColor);
      }
      if (theme.backgroundColor) {
        root.style.setProperty('--fanks-background', theme.backgroundColor);
      }
      if (theme.textColor) {
        root.style.setProperty('--fanks-foreground', theme.textColor);
      }
    }
  }, [theme]);

  // Listen for messages from parent
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'fanks:opened') {
        // Widget was opened
      } else if (event.data.type === 'fanks:identify') {
        // Handle customer identification
        console.log('Identified customer:', event.data.customer);
      }
    };

    window.addEventListener('message', handleMessage);

    // Notify parent that widget is ready
    window.parent.postMessage({ type: 'fanks:ready' }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (!stackId || !customerId) {
    return (
      <div className="flex items-center justify-center h-screen p-4 text-center">
        <p className="text-gray-500">Widget configuration error</p>
      </div>
    );
  }

  return (
    <ChatProvider stackId={stackId} customerId={customerId} apiKey={apiKey}>
      <AppContent />
    </ChatProvider>
  );
}
